/**
 * Thinking Mode Panel - Integrated into Chat Pane
 * Shows real-time agent operations, code generation, and file changes
 */

import { useState, useRef, useEffect } from 'react'
import { Loader2, CheckCircle2, ChevronDown, FileText, Undo2, X, Check, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import hljs from 'highlight.js'

// Types matching chat-pane.tsx state
interface ThinkingOperation {
  id: string
  type: 'search' | 'search_files' | 'read' | 'mcp_tool' | 'tool_call' | 'persona'
  label: string
  status: 'active' | 'completed' | 'error'
  result?: string
  timestamp?: string
  /** Soul / agent name when type === 'persona' */
  personaName?: string
  /** Short query or path hint */
  personaHint?: string
}

interface CurrentTask {
  action: string
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
  filePath: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  content: string
  isWriting: boolean
  timestamp?: string
}

interface CompletedFile {
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
  filePath: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  status: 'new' | 'modified'
  linesAdded: number
  linesRemoved: number
  documentId?: string
  timestamp?: string
}

interface ThinkingModePanelProps {
  thinkingOperations: ThinkingOperation[]
  currentTask: CurrentTask | null
  completedFiles: CompletedFile[]
  agenticRound?: { current: number; max: number } | null
  onViewFile?: (filePath: string) => void
  onUndoAll?: () => void
  onKeepAll?: () => void
}


// Soul / persona loading — visually distinct from generic "Searching: …" rows
function PersonaSubtask({ task }: { task: ThinkingOperation }) {
  const title = task.personaName
    ? `Persona: ${task.personaName}`
    : 'Loading persona & context'
  return (
    <div
      className="rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-2.5 text-sm"
      data-testid={testIds.chatPane.thinkingPersonaRow}
    >
      <div className="flex items-start gap-2">
        {task.status === 'active' && (
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-violet-500 animate-pulse" />
        )}
        {task.status === 'completed' && (
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-green-500" />
        )}
      {task.status === 'error' && (
        <div className="w-4 h-4 shrink-0 mt-0.5 rounded-full bg-red-500/80" />
      )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground flex items-center gap-2">
            {title}
            {task.status === 'active' && (
              <Loader2 className="w-3 h-3 animate-spin text-violet-500" />
            )}
          </div>
          {task.personaHint ? (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2" title={task.personaHint}>
              {task.personaHint}
            </p>
          ) : null}
          {task.result ? (
            <div className="text-xs text-muted-foreground mt-1">{task.result}</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Hide generic success footnotes; status icon already conveys completion. */
function operationDetailLine(task: ThinkingOperation): string | null {
  if (!task.result) return null
  const r = task.result.trim()
  if (!r || /^completed$/i.test(r)) return null
  if (task.status === 'error' && /^error:/i.test(r)) return r.replace(/^error:\s*/i, '')
  return r
}

// Single operation row: one line (icon + label + optional detail)
function Subtask({ task }: { task: ThinkingOperation }) {
  const detail = operationDetailLine(task)
  return (
    <div className="flex items-center gap-2 text-xs leading-snug min-w-0">
      {task.status === 'active' && (
        <Loader2 className="w-3 h-3 shrink-0 animate-spin text-muted-foreground" />
      )}
      {task.status === 'completed' && (
        <CheckCircle2 className="w-3 h-3 shrink-0 text-green-500" />
      )}
      {task.status === 'error' && (
        <div className="w-3 h-3 shrink-0 rounded-full bg-red-500/80" />
      )}
      <span className="min-w-0 flex-1 truncate">
        <span
          className={cn(
            task.status === 'error'
              ? 'text-yellow-500'
              : task.status === 'active'
                ? 'text-foreground'
                : 'text-foreground/85',
          )}
        >
          {task.status === 'error' && detail ? `Failed: ${detail}` : task.label}
        </span>
        {task.status !== 'error' && detail ? (
          <>
            <span className="text-muted-foreground/40 mx-1">·</span>
            <span className="text-muted-foreground">{detail}</span>
          </>
        ) : null}
      </span>
    </div>
  )
}

// Code generation box with syntax highlighting
function CodeGenerationBox({ task }: { task: CurrentTask }) {
  const codeRef = useRef<HTMLDivElement>(null)
  const highlightedCodeRef = useRef<HTMLElement>(null)
  const [showCursor, setShowCursor] = useState(true)
  const [language, setLanguage] = useState<string>('plaintext')

  // Detect language from file extension
  useEffect(() => {
    const ext = task.filePath.split('.').pop()?.toLowerCase()
    const langMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'md': 'markdown',
      'json': 'json',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'yml': 'yaml',
      'yaml': 'yaml',
      'sh': 'bash',
      'bash': 'bash',
    }
    setLanguage(langMap[ext || ''] || 'plaintext')
  }, [task.filePath])

  // Apply syntax highlighting when content changes
  useEffect(() => {
    if (highlightedCodeRef.current && task.content && !task.isWriting) {
      try {
        hljs.highlightElement(highlightedCodeRef.current)
      } catch (error) {
        console.warn('[CodeGenerationBox] Highlight error:', error)
      }
    }
  }, [task.content, task.isWriting])

  // Auto-scroll only if user is near bottom
  useEffect(() => {
    if (codeRef.current && task.isWriting) {
      const element = codeRef.current
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50
      if (isNearBottom) {
        element.scrollTop = element.scrollHeight
      }
    }
  }, [task.content, task.isWriting])

  // Cursor blink animation
  useEffect(() => {
    if (task.isWriting) {
      const timer = setInterval(() => {
        setShowCursor(prev => !prev)
      }, 530)
      return () => clearInterval(timer)
    }
  }, [task.isWriting])

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border flex items-center justify-between">
        {/* Never render task.filePath directly — it's an internal storage path (e.g. ws/{orgId}/{slug}.md). */}
        <span className="text-sm" title={task.url || undefined}>
          {task.displayPath || task.filePath.split('/').pop()}
        </span>
        {language !== 'plaintext' && (
          <span className="text-xs text-muted-foreground uppercase">{language}</span>
        )}
      </div>
      <div
        ref={codeRef}
        className="chat-code-block bg-muted p-4 font-mono text-xs overflow-auto text-foreground"
        style={{ maxHeight: '200px' }}
      >
        {task.isWriting ? (
          <pre className="whitespace-pre-wrap text-foreground/90">
            {task.content}
            {showCursor && (
              <span className="animate-pulse">|</span>
            )}
          </pre>
        ) : (
          <pre>
            <code ref={highlightedCodeRef} className={`language-${language}`}>
              {task.content}
            </code>
          </pre>
        )}
      </div>
    </div>
  )
}

// File diff item (matches prototype SummaryView style)
function FileDiffItem({
  file,
  onViewFile,
  onUndo,
  onKeep
}: {
  file: CompletedFile
  onViewFile?: (filePath: string) => void
  onUndo?: (filePath: string) => void
  onKeep?: (filePath: string) => void
}) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer group"
      onClick={() => onViewFile?.(file.filePath)}
      title={file.displayPath || file.url || undefined}
    >
      {/* File icon */}
      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />

      {/* File name (truncated) - smaller font, normal (not monospace) */}
      <span className="text-xs flex-1 truncate">
        {file.filePath.split('/').pop()}
      </span>

      {/* Line changes - green/red */}
      <div className="flex items-center gap-2 text-xs">
        {file.linesAdded > 0 && (
          <span className="text-green-500">+{file.linesAdded}</span>
        )}
        {file.linesRemoved > 0 && (
          <span className="text-red-500">-{file.linesRemoved}</span>
        )}
      </div>

      {/* Keep/Undo buttons */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onUndo && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onUndo(file.filePath)
            }}
            className="p-1 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-500 transition-colors"
            title="Undo changes"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {onKeep && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onKeep(file.filePath)
            }}
            className="p-1 hover:bg-green-500/10 rounded text-muted-foreground hover:text-green-500 transition-colors"
            title="Keep changes"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export function ThinkingModePanel({
  thinkingOperations,
  currentTask,
  completedFiles,
  agenticRound,
  onViewFile,
  onUndoAll,
  onKeepAll
}: ThinkingModePanelProps) {
  // Check early return condition BEFORE any hooks
  const hasActiveOperations = thinkingOperations.some(op => op.status === 'active')
  const hasCompletedOperations = thinkingOperations.some(op => op.status === 'completed')
  const hasAnyOperations = thinkingOperations.length > 0 || currentTask || completedFiles.length > 0
  const allPersonaOps =
    thinkingOperations.length > 0 && thinkingOperations.every(op => op.type === 'persona')
  const anyPersonaOps = thinkingOperations.some(op => op.type === 'persona')
  
  // Don't show "Planning next moves..." if there are no operations at all
  // MUST CHECK THIS BEFORE HOOKS to avoid hooks violation
  if (!hasAnyOperations && !hasActiveOperations) {
    return null
  }

  // Now safe to use hooks after early return check
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['operations']))

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const mainAction = currentTask?.action ||
    (hasActiveOperations && allPersonaOps
      ? 'Loading persona & context...'
      : hasActiveOperations
        ? 'Executing operations...'
        : 'Planning next moves...')

  // When operations exist, the collapsible list below is the status UI — avoid a second
  // standalone "Executing operations..." row above it (same label, redundant spinner).
  const showTopPlanningRow = thinkingOperations.length === 0

  return (
    <div className="border-t border-border pt-3 space-y-3 fade-in slide-in-from-bottom-2">
      {/* Planning indicator - only before first operation arrives */}
      {showTopPlanningRow && (
        <div className="flex items-center gap-2">
          {currentTask ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground animate-pulse">
                {mainAction}
              </span>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '200ms' }} />
                <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '400ms' }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Operations list - collapsible like prototype */}
      {thinkingOperations.length > 0 && (
        <div>
          <button
            onClick={() => toggleSection('operations')}
            className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
          >
            <div className="flex items-center gap-2">
              {hasCompletedOperations && !hasActiveOperations ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : hasActiveOperations ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : null}
              <span className="text-sm text-muted-foreground">
                {hasActiveOperations
                  ? anyPersonaOps && allPersonaOps
                    ? 'Persona & context'
                    : anyPersonaOps
                      ? 'Operations & persona'
                      : 'Executing operations'
                  : anyPersonaOps && allPersonaOps
                    ? 'Persona & context'
                    : 'Operations'}{' '}
                ({thinkingOperations.length})
                {agenticRound && agenticRound.current > 1 ? (
                  <span className="text-muted-foreground/80">
                    {' '}
                    · round {agenticRound.current}/{agenticRound.max}
                  </span>
                ) : null}
              </span>
            </div>
            {hasCompletedOperations && (
              <ChevronDown className={cn(
                "w-4 h-4 transition-transform text-muted-foreground",
                expandedSections.has('operations') && "rotate-180"
              )} />
            )}
          </button>
          {expandedSections.has('operations') && (
            <div className="pl-6 pb-3 space-y-1.5 mt-1.5 fade-in slide-in-from-top-2">
              <div className="ml-4 space-y-1.5">
                {thinkingOperations.map((op, idx) => (
                  <div
                    key={op.id}
                    className="fade-in slide-in-from-left-2"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    {op.type === 'persona' ? (
                      <PersonaSubtask task={op} />
                    ) : (
                      <Subtask task={op} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!expandedSections.has('operations') && hasCompletedOperations && (
            <div className="pl-6 pb-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
              Completed {thinkingOperations.filter(op => op.status === 'completed').length} operations
            </div>
          )}
        </div>
      )}

      {/* Code generation - collapsible like prototype */}
      {currentTask && (
        <div>
          <button
            onClick={() => toggleSection('code')}
            className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
          >
            <div className="flex items-center gap-2">
              {currentTask.isWriting ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              )}
              <span className="text-sm text-muted-foreground">
                {currentTask.action}
              </span>
            </div>
            {!currentTask.isWriting && (
              <ChevronDown className={cn(
                "w-4 h-4 transition-transform text-muted-foreground",
                expandedSections.has('code') && "rotate-180"
              )} />
            )}
          </button>
          {expandedSections.has('code') && (
            <div className="pl-6 pb-4 space-y-4 mt-2 fade-in slide-in-from-top-2">
              <div className="font-semibold text-base">
                {currentTask.action}
              </div>
              <CodeGenerationBox task={currentTask} />
            </div>
          )}
          {!expandedSections.has('code') && !currentTask.isWriting && (
            <div className="pl-6 pb-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
              Created {currentTask.filePath.split('/').pop()}
            </div>
          )}
        </div>
      )}

      {/* Completed files summary */}
      {completedFiles.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden bg-card">
          <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-sm font-medium">{completedFiles.length} File{completedFiles.length !== 1 ? 's' : ''}</span>
            <div className="flex gap-2">
              {onUndoAll && (
                <Button variant="outline" size="sm" onClick={onUndoAll}>
                  <Undo2 className="w-3 h-3 mr-1" />
                  Undo all
                </Button>
              )}
              {onKeepAll && (
                <Button variant="outline" size="sm" onClick={onKeepAll}>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Keep All
                </Button>
              )}
              <Button size="sm" onClick={() => console.log('Review files')}>
                Review
              </Button>
            </div>
          </div>
          <div className="divide-y divide-border">
            {completedFiles.map((file, idx) => (
              <FileDiffItem
                key={idx}
                file={file}
                onViewFile={onViewFile}
                onUndo={(filePath) => {
                  console.log('[ThinkingMode] Undo file:', filePath)
                  // TODO: Implement undo logic
                }}
                onKeep={(filePath) => {
                  console.log('[ThinkingMode] Keep file:', filePath)
                  // TODO: Implement keep logic
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Show file diff view for completed files (if we have content) */}
      {completedFiles.length > 0 && completedFiles[0] && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-500">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-semibold text-sm">
              {completedFiles[0].status === 'new' ? 'Created' : 'Updated'} {completedFiles[0].filePath.split('/').pop()}
            </span>
          </div>
          {/* Note: FileDiffWithGutter would need actual file content from backend */}
          {/* For now, we show the summary view above */}
        </div>
      )}
    </div>
  )
}

